import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { areaService } from '../services/areaService';
import { AreaTarea, CreateAreaTareaForm, ReordenAreaTareaForm, UpdateAreaTareaConfigForm } from '../types';

export const useAreaTareas = (areaId: number) => {
  const queryClient = useQueryClient();

  const { data: areaTareas = [], isLoading, error, refetch } = useQuery({
    queryKey: ['area-tareas', areaId],
    queryFn: async (): Promise<AreaTarea[]> => {
      const resp = await areaService.listarAreaTareas(areaId);
      if (!resp.success) throw new Error(resp.message || 'Error al cargar tareas del área');
      return resp.data || [];
    },
    enabled: !!areaId,
  });

  const addMutation = useMutation({
    mutationFn: async (payload: CreateAreaTareaForm) => {
      const resp = await areaService.agregarAreaTarea(areaId, payload);
      if (!resp.success) throw new Error(resp.message || 'Error al agregar tarea');
      return resp.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-tareas', areaId] }),
  });

  const excludeMutation = useMutation({
    mutationFn: async (tareaId: number) => {
      const resp = await areaService.excluirAreaTarea(areaId, tareaId);
      if (!resp.success) throw new Error(resp.message || 'Error al excluir tarea');
      return resp;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-tareas', areaId] }),
  });

  const reinstateMutation = useMutation({
    mutationFn: async (tareaId: number) => {
      const resp = await areaService.reincorporarAreaTarea(areaId, tareaId);
      if (!resp.success) throw new Error(resp.message || 'Error al reincorporar tarea');
      return resp;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-tareas', areaId] }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (ordenes: ReordenAreaTareaForm[]) => {
      const resp = await areaService.reordenarAreaTareas(areaId, ordenes);
      if (!resp.success) throw new Error(resp.message || 'Error al reordenar tareas');
      return resp;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-tareas', areaId] }),
  });

  const updateConfigMutation = useMutation({
    mutationFn: async ({ tareaId, payload }: { tareaId: number; payload: UpdateAreaTareaConfigForm }) => {
      const resp = await areaService.actualizarConfigAreaTarea(areaId, tareaId, payload);
      if (!resp.success) throw new Error(resp.message || 'Error al actualizar configuración');
      return resp;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-tareas', areaId] }),
  });
  
  const deletePersonalizadaMutation = useMutation({
    mutationFn: async (tareaId: number) => {
      const resp = await areaService.eliminarAreaTareaPersonalizada(areaId, tareaId);
      if (!resp.success) throw new Error(resp.message || 'Error al eliminar tarea personalizada');
      return resp;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['area-tareas', areaId] }),
  });

  return {
    areaTareas,
    isLoading,
    error,
    refetch,
    addAreaTarea: addMutation.mutate,
    excludeAreaTarea: excludeMutation.mutate,
    reinstateAreaTarea: reinstateMutation.mutate,
    reorderAreaTareas: reorderMutation.mutate,
    updateAreaTareaConfig: updateConfigMutation.mutate,
    deleteAreaTareaPersonalizada: deletePersonalizadaMutation.mutate,
    isAdding: addMutation.isPending,
    isExcluding: excludeMutation.isPending,
    isReinstating: reinstateMutation.isPending,
    isReordering: reorderMutation.isPending,
    isUpdatingConfig: updateConfigMutation.isPending,
    isDeleting: deletePersonalizadaMutation.isPending,
  };
};


